import {
  galleryFolderListSchema,
  galleryFolderSchema,
  GALLERY_MEDIA_ENTITY_TYPE,
  type CreateGalleryFolderInput,
  type GalleryFolder,
  type GalleryFolderList,
  type UpdateGalleryFolderInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';
import { mediaListSchema, type Media } from '../media/media-contract.js';

/**
 * Every gallery call the app makes.
 *
 * Every URL string the gallery and the design board need lives in this file and
 * `design-board-api.ts` and nowhere else, so a path task 16 lands differently only ever changes
 * these two files. Media shapes are never redeclared here — `mediaListSchema` is imported from
 * the media module's own contract, the same one the uploader and the lightbox already parse
 * with.
 */

const FOLDERS = '/gallery/folders';

/** The whole folder tree, unpaginated — see `galleryFolderListSchema` for why. */
export async function listGalleryFolders(client: ApiClient): Promise<GalleryFolderList> {
  return await client.get(FOLDERS, { schema: galleryFolderListSchema });
}

/** `kind` is never sent — everything a shop creates is `custom`. */
export async function createGalleryFolder(
  client: ApiClient,
  input: CreateGalleryFolderInput,
): Promise<GalleryFolder> {
  return await client.post(FOLDERS, input, { schema: galleryFolderSchema });
}

/** Renaming and, for a custom folder, reparenting. `kind` is never updatable. */
export async function updateGalleryFolder(
  client: ApiClient,
  folderId: string,
  patch: UpdateGalleryFolderInput,
): Promise<GalleryFolder> {
  return await client.patch(`${FOLDERS}/${folderId}`, patch, { schema: galleryFolderSchema });
}

/** Offered on a custom folder only — the page never renders this for a system one. */
export async function deleteGalleryFolder(client: ApiClient, folderId: string): Promise<void> {
  await client.del(`${FOLDERS}/${folderId}`);
}

export interface ListGalleryMediaParams {
  /** Absent together with `unfiled` means "give me the id-less shop images". */
  folderId?: string;
  unfiled?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * One folder's images, or the unfiled ones, a page at a time.
 *
 * `entityType` is always the exported constant, never the literal `'gallery'` — see the module
 * doc for why that matters.
 */
export async function listGalleryMedia(
  client: ApiClient,
  params: ListGalleryMediaParams = {},
): Promise<Media[]> {
  const { items } = await client.get('/media', {
    schema: mediaListSchema,
    query: {
      entityType: GALLERY_MEDIA_ENTITY_TYPE,
      ...(params.unfiled ? { unfiled: true } : {}),
      ...(!params.unfiled && params.folderId ? { folderId: params.folderId } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 24,
    },
  });
  return items;
}

export interface MoveMediaToFolderInput {
  mediaIds: string[];
  /** `null` unfiles the whole selection. */
  folderId: string | null;
}

/** One call files (or unfiles) a whole selection, from a folder or from the unfiled view alike. */
export async function moveMediaToFolder(
  client: ApiClient,
  input: MoveMediaToFolderInput,
): Promise<Media[]> {
  const { items } = await client.patch('/media/folder', input, { schema: mediaListSchema });
  return items;
}
